import { faEraser } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { useTheme } from '../theme-provider';
import { Button } from '../button';

export interface SignaturePadRef {
  /** Limpia la firma */
  clear: () => void;
  /** Verifica si el pad está vacío */
  isEmpty: () => boolean;
  /** Obtiene la firma como imagen base64 (PNG) */
  toDataURL: (type?: string, encoderOptions?: number) => string;
  /** Obtiene la firma como Blob para subir a servidor */
  toBlob: () => Promise<Blob | null>;
  /** Obtiene la firma como Blob JPG para multipart/form-data (calidad 0.9) */
  toBlobJpeg: (quality?: number) => Promise<Blob | null>;
}

export interface SignaturePadProps {
  /** Ancho del canvas (default: 100%) */
  width?: number | string;
  /** Alto del canvas (default: 200px) */
  height?: number;
  /** Color del trazo (default: #000000) */
  penColor?: string;
  /** Grosor del trazo (default: 2) */
  minWidth?: number;
  maxWidth?: number;
  /** Clase CSS adicional para el contenedor */
  className?: string;
  /** Placeholder cuando está vacío */
  placeholder?: string;
  /** Callback cuando cambia la firma */
  onChange?: (isEmpty: boolean) => void;
  /** Deshabilitado */
  disabled?: boolean;
  /** Mostrar botones de acción (limpiar) */
  showActions?: boolean;
  /** Etiqueta del componente */
  label?: string;
  /** Mensaje de error */
  error?: string;
}

/** Color del trazo en modo claro (texto sobre fondo claro) */
const PEN_COLOR_LIGHT = '#02040a';
/** Color del trazo en modo oscuro (texto sobre fondo oscuro) */
const PEN_COLOR_DARK = '#ced7ed';

type SignatureData = ReturnType<SignatureCanvas['toData']>;

type SignatureSnapshot = {
  data: SignatureData;
  width: number;
  height: number;
};

const cloneSignatureData = (data: SignatureData): SignatureData =>
  data.map((group) => group.map((point) => ({ ...point }) as SignatureData[number][number]));

const getCanvasCssSize = (canvas: HTMLCanvasElement) => {
  const rect = canvas.getBoundingClientRect();

  return {
    width: canvas.offsetWidth || rect.width,
    height: canvas.offsetHeight || rect.height,
  };
};

const getScaledSignatureData = (
  { data, width, height }: SignatureSnapshot,
  nextSize: { width: number; height: number },
): SignatureData => {
  const scaleX = width > 0 && nextSize.width > 0 ? nextSize.width / width : 1;
  const scaleY = height > 0 && nextSize.height > 0 ? nextSize.height / height : 1;

  if (scaleX === 1 && scaleY === 1) {
    return cloneSignatureData(data);
  }

  return data.map((group) =>
    group.map(
      (point) =>
        ({
          ...point,
          x: point.x * scaleX,
          y: point.y * scaleY,
        }) as SignatureData[number][number],
    ),
  );
};

const resizeCanvasBackingStore = (canvas: HTMLCanvasElement, size: { width: number; height: number }) => {
  const ratio = Math.max(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 1);
  const nextWidth = Math.max(Math.round(size.width * ratio), 1);
  const nextHeight = Math.max(Math.round(size.height * ratio), 1);

  if (canvas.width === nextWidth && canvas.height === nextHeight) {
    return false;
  }

  canvas.width = nextWidth;
  canvas.height = nextHeight;
  canvas.getContext('2d')?.scale(ratio, ratio);

  return true;
};

const SignaturePad = forwardRef<SignaturePadRef, SignaturePadProps>(
  (
    {
      width = '100%',
      height = 200,
      penColor: penColorProp,
      minWidth = 1,
      maxWidth = 3,
      className = '',
      placeholder = 'Firma aquí',
      onChange,
      disabled = false,
      showActions = true,
      label,
      error,
    },
    ref,
  ) => {
    const { theme } = useTheme();
    const sigCanvas = useRef<SignatureCanvas>(null);
    const [isEmpty, setIsEmpty] = useState(true);
    const canvasStateRef = useRef<string | null>(null); // Guardar estado del canvas como base64
    const signatureSnapshotRef = useRef<SignatureSnapshot | null>(null);

    // Trazo adaptado al tema: dark = trazo claro, light/system = trazo oscuro
    const penColor = useMemo(() => {
      if (penColorProp) return penColorProp;
      const resolvedDark =
        theme === 'dark' ||
        (theme === 'system' &&
          typeof window !== 'undefined' &&
          window.matchMedia('(prefers-color-scheme: dark)').matches);
      return resolvedDark ? PEN_COLOR_DARK : PEN_COLOR_LIGHT;
    }, [theme, penColorProp]);

    const handleClear = useCallback(() => {
      sigCanvas.current?.clear();
      canvasStateRef.current = null;
      signatureSnapshotRef.current = null;
      setIsEmpty(true);
      onChange?.(true);
    }, [onChange]);

    const restoreCanvas = useCallback(
      (force = false) => {
        if (!sigCanvas.current) return;

        const snapshot = signatureSnapshotRef.current;
        const dataURL = canvasStateRef.current;

        if (!snapshot && !dataURL) return;
        if (!force && !sigCanvas.current.isEmpty()) return;

        const canvas = sigCanvas.current.getCanvas();
        const size = getCanvasCssSize(canvas);

        if (snapshot) {
          sigCanvas.current.fromData(getScaledSignatureData(snapshot, size));
          canvasStateRef.current = sigCanvas.current.toDataURL();
        } else if (dataURL) {
          sigCanvas.current.fromDataURL(dataURL, size);
        }

        setIsEmpty(false);
        onChange?.(false);
      },
      [onChange],
    );

    const syncCanvasSize = useCallback(() => {
      if (!sigCanvas.current) return;

      const canvas = sigCanvas.current.getCanvas();
      const size = getCanvasCssSize(canvas);
      const resized = resizeCanvasBackingStore(canvas, size);

      if (!resized) return;

      if (signatureSnapshotRef.current || canvasStateRef.current) {
        restoreCanvas(true);
      } else {
        sigCanvas.current.clear();
      }
    }, [restoreCanvas]);

    const handleEnd = useCallback(() => {
      const empty = sigCanvas.current?.isEmpty() ?? true;
      setIsEmpty(empty);

      // Guardar estado del canvas para restaurarlo si se pierde (especialmente en móvil)
      if (!empty && sigCanvas.current) {
        try {
          const canvas = sigCanvas.current.getCanvas();
          const size = getCanvasCssSize(canvas);
          signatureSnapshotRef.current = {
            data: cloneSignatureData(sigCanvas.current.toData()),
            ...size,
          };
          const dataURL = sigCanvas.current.toDataURL();
          canvasStateRef.current = dataURL;
        } catch (error) {
          console.warn('Error al guardar estado del canvas:', error);
        }
      } else {
        canvasStateRef.current = null;
        signatureSnapshotRef.current = null;
      }

      onChange?.(empty);
    }, [onChange]);

    // Restaurar el estado del canvas si se pierde (útil en móvil cuando se hace scroll)
    useEffect(() => {
      const canvas = sigCanvas.current?.getCanvas();
      if (!canvas) return;

      let restoreTimeout: ReturnType<typeof setTimeout> | null = null;
      let isRestoring = false;

      // Función para restaurar el canvas
      const restoreCanvasAfterViewportChange = () => {
        if (isRestoring || !sigCanvas.current) return;
        syncCanvasSize();
        restoreCanvas();
        if (!canvasStateRef.current) return;

        const currentlyEmpty = sigCanvas.current.isEmpty();
        if (currentlyEmpty && !isEmpty) {
          isRestoring = true;
          const img = new Image();
          img.onload = () => {
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0);
              setIsEmpty(false);
              onChange?.(false);
            }
            isRestoring = false;
          };
          img.onerror = () => {
            isRestoring = false;
          };
          img.src = canvasStateRef.current;
        }
      };

      // Restaurar cuando el componente vuelve a ser visible (útil después de scroll)
      const handleVisibilityChange = () => {
        if (!document.hidden && canvasStateRef.current) {
          restoreTimeout = setTimeout(restoreCanvasAfterViewportChange, 100);
        }
      };

      const handleViewportChange = () => {
        if (restoreTimeout) clearTimeout(restoreTimeout);
        restoreTimeout = setTimeout(restoreCanvasAfterViewportChange, 100);
      };

      // Restaurar cuando el canvas vuelve al viewport (Intersection Observer)
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && canvasStateRef.current) {
              restoreTimeout = setTimeout(restoreCanvasAfterViewportChange, 100);
            }
          });
        },
        { threshold: 0.1 },
      );

      if (canvas) {
        observer.observe(canvas);
      }

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('resize', handleViewportChange);
      window.visualViewport?.addEventListener('resize', handleViewportChange);

      return () => {
        if (restoreTimeout) clearTimeout(restoreTimeout);
        observer.disconnect();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('resize', handleViewportChange);
        window.visualViewport?.removeEventListener('resize', handleViewportChange);
      };
    }, [isEmpty, onChange, restoreCanvas, syncCanvasSize]);

    const ensureSignatureRendered = useCallback(() => {
      if (!signatureSnapshotRef.current && !canvasStateRef.current) return;

      syncCanvasSize();
      restoreCanvas(true);
    }, [restoreCanvas, syncCanvasSize]);

    useImperativeHandle(ref, () => ({
      clear: handleClear,
      isEmpty: () => {
        const canvasIsEmpty = sigCanvas.current?.isEmpty() ?? true;
        return canvasIsEmpty && !signatureSnapshotRef.current && !canvasStateRef.current;
      },
      toDataURL: (type = 'image/png', encoderOptions = 1) => {
        if (sigCanvas.current?.isEmpty() && (signatureSnapshotRef.current || canvasStateRef.current)) {
          ensureSignatureRendered();
        }
        return sigCanvas.current?.toDataURL(type, encoderOptions) ?? '';
      },
      toBlob: async () => {
        ensureSignatureRendered();
        const canvas = sigCanvas.current?.getCanvas();
        if (!canvas) return null;

        return new Promise<Blob | null>((resolve) => {
          canvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/png');
        });
      },
      toBlobJpeg: async (quality = 0.9) => {
        ensureSignatureRendered();
        const canvas = sigCanvas.current?.getCanvas();
        if (!canvas) return null;

        return new Promise<Blob | null>((resolve) => {
          canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
        });
      },
    }));

    return (
      <div className={`space-y-2 ${className}`}>
        {label && <label className="block text-sm font-medium text-foreground">{label}</label>}

        <div className="relative">
          <div
            className={`
              relative border rounded-lg overflow-hidden bg-white dark:bg-card-bg
              ${disabled ? 'opacity-50 pointer-events-none' : ''}
              ${error ? 'border-destructive' : 'border-border'}
              transition-colors
            `}
            style={{
              width,
              height,
              touchAction: 'none', // Prevenir scroll del contenedor mientras se dibuja
              WebkitOverflowScrolling: 'touch', // Scroll suave en iOS
            }}
          >
            <SignatureCanvas
              ref={sigCanvas}
              penColor={penColor}
              minWidth={minWidth}
              maxWidth={maxWidth}
              clearOnResize={false}
              canvasProps={{
                className: 'w-full h-full touch-none',
                style: {
                  width: '100%',
                  height: '100%',
                  cursor: disabled ? 'not-allowed' : 'crosshair',
                  touchAction: 'none', // Prevenir scroll mientras se dibuja
                  WebkitTouchCallout: 'none', // Prevenir menú contextual en iOS
                  WebkitUserSelect: 'none', // Prevenir selección en iOS
                  userSelect: 'none',
                  willChange: 'contents', // Optimización para GPU
                },
              }}
              onEnd={handleEnd}
            />

            {/* Placeholder */}
            {isEmpty && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-muted-foreground text-sm">{placeholder}</span>
              </div>
            )}

            {/* Línea guía para firma - visible en light y dark */}
            <div className="absolute bottom-8 left-4 right-4 border-b border-dashed border-muted-foreground/30 dark:border-muted-foreground/40 pointer-events-none" />
          </div>

          {/* Botones de acción */}
          {showActions && !disabled && (
            <div className="absolute top-2 right-2 flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClear}
                disabled={isEmpty}
                className="h-8 w-8 p-0 bg-white/80 hover:bg-white dark:bg-body-bg dark:hover:bg-hover-press shadow-sm text-foreground"
                title="Limpiar firma"
              >
                <FontAwesomeIcon icon={faEraser} className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    );
  },
);

SignaturePad.displayName = 'SignaturePad';

export { SignaturePad };
